
import { Options, Vue } from 'vue-class-component'
import { Prop, Watch } from 'vue-property-decorator'
import { maska } from 'maska'
import { IDateRangePicker, IPopup } from '@/utils/types'
import { Formater } from '@/utils/formater'
import { ACTION_POPUP } from '@/store/actions'
import { DatetimeUtils, lastNMonth, now } from '@/utils/datetime'
import DateRangeSelectorV2 from '@/components/common/ui/DateRangeSelectorV2.vue'
import PopupModal from '@/components/story/common/PopupModal.vue'
import MessagesFormModal from '@/components/messages/MessagesFormModal.vue'
import { constant } from '@/utils/constants'
import MediaDetailsModal from '@/components/media-manager/MediaDetailsModal.vue'
import cloneDeep from 'lodash/cloneDeep'

@Options({
  components: { MediaDetailsModal, MessagesFormModal, PopupModal, DateRangeSelectorV2 },
  directives: { maska },
  emits: [
    'update:modelValue',
    'update:addPopup',
    'update:updatePopup',
    'update:activePopup',
    'update:isSettingValue',
    'update:selectedPopup',
  ],
})
export default class PopupListView extends Vue {
  @Prop()
  popupList!: IPopup[]

  @Prop({})
  isTestMode!: boolean

  @Prop()
  isSetting!: boolean

  @Prop()
  appId!: string

  @Prop()
  campaignId!: string

  items: IPopup[] = []
  popupModalVisible = false
  modalMediaVisible = false
  maxActivePopup = constant.STORY_POPUP.MAX_ACTIVATED

  dateRange: IDateRangePicker = {
    startDate: '',
    endDate: '',
  }

  blankPopup: IPopup = {
    _id: '',
    app_id: '',
    campaign_id: '',
    is_active: false,
    is_test_mode: false,
    display_id: '',
    popup_name: '',
    images: [],
    animation_type: constant.animation_type.fade_in,
  }

  selectedPopup: IPopup = cloneDeep(this.blankPopup)

  get setting() {
    return this.isSetting
  }

  set setting(value: boolean) {
    this.$emit('update:isSettingValue', value)
  }

  get activePopupCount() {
    if (this.isTestMode) {
      return this.items.filter((item) => item.is_test_mode === true).length
    }
    return this.items.filter((item) => item.is_active === true).length
  }

  @Watch('isTestMode', { immediate: true })
  @Watch('popupList', { deep: true })
  async loadData() {
    if (this.appId && this.campaignId) {
      this.items = await this.$store.dispatch(ACTION_POPUP.LOAD_ITEMS, {
        app_id: this.appId,
        campaign_id: this.campaignId,
        is_test_mode: this.isTestMode,
        date_from: DatetimeUtils.formatDate(this.dateRange.startDate),
        date_to: DatetimeUtils.formatDate(this.dateRange.endDate),
      })
    }
  }

  @Watch('selectedPopup', { immediate: true })
  onSelectedPopup(data: IPopup) {
    this.$emit('update:selectedPopup', data)
  }

  onDetails(event, data: IPopup) {
    // Check if Ctrl (Windows) or Meta (Mac) is pressed
    if (event.ctrlKey || event.metaKey) {
      // Let the browser handle opening in a new tab
      return
    }
    // Otherwise, navigate programmatically
    event.preventDefault()

    this.goto('popup_details', {
      app_id: this.appId,
      campaign_id: data.campaign_id,
      popup_id: data._id,
      from_date: lastNMonth(1),
      to_date: now(),
    })
  }

  getDetailsUrl(data) {
    // Construct the URL as a string for direct navigation
    return this.$router.resolve({
      name: 'popup_details',
      params: {
        app_id: this.appId as string,
        campaign_id: data.campaign_id,
        popup_id: data._id,
        from_date: lastNMonth(1),
        to_date: now(),
      },
    }).href
  }

  onMediaDetails(data: IPopup) {
    this.selectedPopup = data
    this.modalMediaVisible = true
  }

  goto(name: string, params = {}) {
    this.$router.push({
      name,
      params,
    })
  }

  async handleToggleActive(record: IPopup) {
    if (this.activePopupCount > this.maxActivePopup && this.statusMode(record)) {
      if (this.isTestMode) {
        record.is_test_mode = false
      } else {
        record.is_active = false
      }
      this.$q.dialog({
        title: this.$t('messages.confirm_please_note'),
        message: this.$t('messages.please_diasble_popup_story'),
        cancel: {
          flat: true,
          label: this.$t('cancel'),
        },
        persistent: true,
        html: true,
      })
      return
    }
    this.$emit('update:activePopup', record)
  }

  statusMode(item: IPopup) {
    if (this.isTestMode) {
      return item.is_test_mode ?? false
    } else {
      return item.is_active ?? false
    }
  }

  changeDateRange(value) {
    this.dateRange = value
    this.loadData()
  }

  onAdd() {
    this.selectedPopup = cloneDeep(this.blankPopup)
    this.popupModalVisible = true
    this.setting = true
  }

  onEdit(data: IPopup) {
    this.selectedPopup = data
    this.popupModalVisible = true
  }

  onAddPopup(data: IPopup) {
    this.$emit('update:addPopup', data)
  }

  onUpdatePopup(data: IPopup) {
    this.$emit('update:updatePopup', data)
  }

  onCloseModal() {
    this.popupModalVisible = false
  }

  numberWithCommas(num) {
    return Formater.numberFormat(num)
  }

  roundNumber(num) {
    return Formater.round(num)
  }

  mounted() {
    this.dateRange = DatetimeUtils.getDateRange('last30Days')
    this.loadData()
  }
}
